/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Table, Tag, Progress, Tooltip, DatePicker, Select, Modal } from "antd";
import { useEffect, useState } from "react";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";

import dayjs from "dayjs";

import useExcursions from "../hooks/useExcursions";
import { useNavigate } from "react-router";
import { ExternalLink } from "lucide-react";
import ScheduleExcursionFormModal from "./ScheduleExcursionFormModal";

const statusFormat = {
  IN_PROGRESS: { text: "En progreso", color: "blue", icon: <SyncOutlined /> },
  FINISHED: {
    text: "Finalizada",
    color: "green",
    icon: <CheckCircleOutlined />,
  },
  PENDING: {
    text: "Pendiente",
    color: "orange",
    icon: <ClockCircleOutlined />,
  },
  STOP_SELLING: {
    text: "No hay más lugar",
    color: "red",
    icon: <ClockCircleOutlined />,
  },
};

function ExcursionTable({ onExcursionSelect }) {
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [params, setParams] = useState({});
  const [dateRange, setDateRange] = useState(null);
  const { excursions, getExcursions, editScheduledExcursion } = useExcursions();
  const [modalVisible, setModalVisible] = useState(false);
  const [editData, setEditData] = useState(null);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);

  useEffect(() => {
    getExcursions(params);
  }, [params]);

  const showModal = (data = null) => {
    setEditData(data);
    setModalVisible(true);
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditData(null);
    // Refresh the excursions list after creating/editing
    getExcursions(params);
  };

  const handleStatusChange = async (excursionId, newStatus) => {
    if (newStatus === "FINISHED") {
      setPendingStatusChange({ excursionId, newStatus });
      return;
    }
    await editScheduledExcursion({ id: excursionId, status: newStatus });
    getExcursions(params);
  };

  const confirmStatusChange = async () => {
    if (pendingStatusChange) {
      await editScheduledExcursion({ id: pendingStatusChange.excursionId, status: pendingStatusChange.newStatus });
      getExcursions(params);
      setPendingStatusChange(null);
    }
  };

  const columns = [
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        return (
          <Select
            value={status}
            onChange={(newStatus) => handleStatusChange(record.id, newStatus)}
            style={{ width: 160 }}
            size="small"
          >
            {Object.entries(statusFormat).map(([key, { text, color, icon }]) => (
              <Select.Option key={key} value={key}>
                <Tag color={color} icon={icon} style={{ margin: 0 }}>
                  {text}
                </Tag>
              </Select.Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: "Nombre",
      dataIndex: ["excursionType"],
      key: "excursionType",
      render: (excursionType, record) => {
        return (
          <Tooltip title="Click para ver detalle de excursión">
            <Button
              type="link"
              onClick={() => {
                window.open(
                  `${apiUrl}admin/schedule_excursion/scheduledexcursion/${record.id}/change/`,
                  "_blank"
                );
              }}
            >
              {record.excursionType.name}
              <ExternalLink size={12} />
            </Button>
          </Tooltip>
        );
      },
    },
    {
      title: "Fecha",
      dataIndex: "day",
      key: "day",
      render: (day) => {
        const parsedDate = dayjs(day).format("ddd DD MMM");

        return parsedDate;
      },
    },
    {
      title: () => (
        <div>
          <span className="mr-2">Grupo</span>
        </div>
      ),
      dataIndex: "group",
      key: "group",
      render: (group) => group?.name || "-",
    },
    {
      title: "Pasajeros",
      dataIndex: "seats",
      key: "seats",
      render: (seats, record) => {
        console.log(record);
        return (
          <div className="w-full text-center">
            <span className="font-bold">{record.passengersCount}</span>
          </div>
        );
      },
    },
    {
      title: "Butacas totales",
      dataIndex: "seats",
      key: "seats",
      render: (seats, record) => {
        return (
          <div className="w-full text-center">
            <span className="font-bold">{record.seats}</span>
          </div>
        );
      },
    },
    {
      title: "Acciones",
      key: "actions",
      render: (text, record) => {
        return (
          <div className="flex flex-col">
            <Button
              onClick={() => {
                navigate(`/excursiones/${record.id}/buses`);
                onExcursionSelect(record);
              }}
            >
              Armado de Micros
            </Button>
          </div>
        );
      },
    },
  ];

  const handleTableChange = (pagination) => {
    const newParams = { ...params, page: pagination };
    setParams(newParams);
  };

  return (
    <div className="">
      <div className="flex justify-between">
        <span className="font-semibold text-lg">Listado de Excursiones</span>

        <Button type="primary" className="mb-3" onClick={() => showModal()}>
          Crear Excursion
        </Button>
      </div>

      <Table
        columns={columns}
        loading={!excursions.results}
        size="small"
        dataSource={excursions.results}
        pagination={{
          current: params.page || 1,
          pageSize: 10,
          total: excursions.total,
          onChange: handleTableChange,
        }}
        rowKey="id"
        onRow={(record) => ({
          onClick: (e) => {
            // Evitar navegación si se hace clic en elementos interactivos
            if (e.target.closest('.ant-select, .ant-btn, a')) return;
            navigate(`/excursiones/${record.id}/buses`);
            onExcursionSelect(record);
          },
          style: { cursor: 'pointer' },
        })}
      />

      <ScheduleExcursionFormModal
        visible={modalVisible}
        onCancel={handleModalCancel}
        editData={editData}
      />

      <Modal
        title="Confirmar finalización"
        open={!!pendingStatusChange}
        onOk={confirmStatusChange}
        onCancel={() => setPendingStatusChange(null)}
        okText="Sí, finalizar"
        cancelText="Cancelar"
      >
        <p>¿Estás seguro de que deseas finalizar esta excursión?</p>
      </Modal>
    </div>
  );
}

export default ExcursionTable;
